import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTheme } from 'styled-components';
import { getFormattedExportSize } from '../../utils/exportUtils';
import {
  ModalOverlay,
  ModalContainer,
  CloseButton,
  ModalContent,
  ModalSidebar,
  ModalMain,
  SidebarTitle,
  SidebarSubtitle,
  FormatList,
  FormatItem,
  FormatName,
  FormatSize,
  PreviewArea,
  PreviewIcon,
  PreviewTitle,
  PreviewDescription,
  ExportButton,
} from './StyledComponents';

// SVG Icons as components
const CSVIcon = ({ size = 80, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* File outline */}
    <path
      d="M6 2C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2H6Z"
      stroke={color}
      strokeWidth="2"
      fill="none"
    />
    <path
      d="M14 2V8H20"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      fill="none"
    />
    {/* Table grid inside */}
    <rect
      x="7"
      y="11"
      width="10"
      height="8"
      stroke={color}
      strokeWidth="1.5"
      fill="none"
    />
    <line x1="7" y1="15" x2="17" y2="15" stroke={color} strokeWidth="1.5" />
    <line x1="11" y1="11" x2="11" y2="19" stroke={color} strokeWidth="1.5" />
    <line x1="14" y1="11" x2="14" y2="19" stroke={color} strokeWidth="1.5" />
  </svg>
);

const PDFIcon = ({ size = 80, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {/* File shape */}
    <path
      d="M6 2C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2H6Z"
      stroke={color}
      strokeWidth="2"
      fill="none"
    />
    <path
      d="M14 2V8H20"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      fill="none"
    />
    {/* Document lines */}
    <line
      x1="7"
      y1="12"
      x2="17"
      y2="12"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="7"
      y1="15"
      x2="17"
      y2="15"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="7"
      y1="18"
      x2="12"
      y2="18"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const CloseIcon = ({ size = 16, color = '#ffffff' }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill={color}>
    <path
      fillRule="evenodd"
      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

const ExportModal = ({
  isOpen,
  onClose,
  onExport,
  isExporting,
  customerCount,
}) => {
  const theme = useTheme();
  const [selectedFormat, setSelectedFormat] = useState('CSV');

  const handleExport = () => {
    if (selectedFormat && !isExporting) {
      onExport(selectedFormat);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isExporting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const formats = [
    {
      type: 'CSV',
      name: 'CSV File',
      description:
        'Export customer data in CSV format. Perfect for importing into spreadsheet applications, databases, or data analysis tools.',
      Icon: CSVIcon,
    },
    {
      type: 'PDF',
      name: 'PDF Document',
      description:
        'Export customer data as a formatted PDF report. Ideal for presentations, documentation, or sharing with stakeholders.',
      Icon: PDFIcon,
    },
  ];

  const currentFormat = formats.find((f) => f.type === selectedFormat);
  const CurrentIcon = currentFormat.Icon;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <CloseButton onClick={onClose} disabled={isExporting}>
          <CloseIcon />
        </CloseButton>

        <ModalContent>
          <ModalSidebar>
            <SidebarTitle>Export Format</SidebarTitle>
            <SidebarSubtitle>
              Choose how you'd like to export your data
            </SidebarSubtitle>

            <FormatList>
              {formats.map((format) => (
                <FormatItem
                  key={format.type}
                  selected={selectedFormat === format.type}
                  onClick={() => setSelectedFormat(format.type)}
                  disabled={isExporting}
                >
                  <FormatName>{format.name}</FormatName>
                  {customerCount > 0 && (
                    <FormatSize>
                      Estimated size:{' '}
                      {getFormattedExportSize(format.type, customerCount)}
                    </FormatSize>
                  )}
                </FormatItem>
              ))}
            </FormatList>
          </ModalSidebar>

          <ModalMain>
            <PreviewArea>
              <PreviewIcon>
                <CurrentIcon color={theme?.colors?.primary || '#FA4616'} />
              </PreviewIcon>
              <PreviewTitle>{currentFormat.type} Export</PreviewTitle>
              <PreviewDescription>
                {currentFormat.description}
              </PreviewDescription>
              <ExportButton
                onClick={handleExport}
                disabled={!selectedFormat || isExporting}
              >
                {isExporting ? 'Exporting...' : `Export as ${selectedFormat}`}
              </ExportButton>
            </PreviewArea>
          </ModalMain>
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

ExportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  isExporting: PropTypes.bool,
  customerCount: PropTypes.number,
};

ExportModal.defaultProps = {
  isExporting: false,
  customerCount: 0,
};

export default ExportModal;
